import { Container, Title, Text, Badge, SimpleGrid, Card, Group, ThemeIcon, Divider } from "@mantine/core";
import { IconCheck, IconInfinity, IconDeviceGamepad2, IconHeadset } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import classes from "./SubscriptionPage.module.css";
import tvlogo from "../../media/accTV.png";
import acadlogo from "../../media/acc-Academy.png";
import irdlogo from "../../media/iRdrive-logo.png";
import lmudlogo from "../../media/lmudrive-logo.png";
import drivelogo from "../../media/drive-logo.png";
import acdrivelogo from "../../media/drive-logo.png";
import { SubscriptionCheckout } from "./SubscriptionCheckout";

const includedApps = [
  { id: "accdrive", logo: drivelogo, color: "#3B902C" },
  { id: "academy", logo: acadlogo, color: "#FFC03B" },
  { id: "acdrive", logo: acdrivelogo, color: "#3B902C" },
  { id: "irdrive", logo: irdlogo, color: "#2064E9" },
  { id: "lmudrive", logo: lmudlogo, color: "#080C08" },
];

export function SubscriptionPage() {
  const { t } = useTranslation("translation");

  return (
    <div className={classes.pageWrapper}>
      {/* Hero Section */}
      <div className={classes.hero}>
        <div className={classes.heroBackground}>
          <div className={classes.gradientOverlay} />
        </div>
        <Container size="lg" className={classes.heroContent}>
          <Badge variant="filled" color="red" size="lg" mb="md" className={classes.badge}>
            {t("subscription.hero.badge")}
          </Badge>
          <Title className={classes.heroTitle}>
            {t("subscription.hero.title")}
          </Title>
          <Text className={classes.heroSubtitle}>
            {t("subscription.hero.subtitle")}
          </Text>
          <div className={classes.priceBlock}>
            <Text className={classes.priceAmount}>19.99€</Text>
            <Text className={classes.priceLabel}>{t("subscription.hero.perMonth")}</Text>
          </div>
          <Text size="sm" c="dimmed" mt="xs">
            {t("subscription.hero.vatNote")}
          </Text>
        </Container>
      </div>

      {/* Included Apps */}
      <Container size="xl" py="xl">
        <Title order={2} ta="center" mb="sm" className={classes.sectionTitle}>
          {t("subscription.apps.title")}
        </Title>
        <Text ta="center" c="dimmed" mb="xl">
          {t("subscription.apps.subtitle")}
        </Text>

        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
          {includedApps.map((app) => (
            <Card key={app.id} shadow="sm" radius="lg" className={classes.appCard}>
              <div className={classes.appCardLogo} style={{ backgroundColor: app.color }}>
                <img
                  src={app.logo}
                  alt={t(`subscription.apps.${app.id}.name`)}
                  className={classes.appLogo}
                />
              </div>
              <div className={classes.appCardBody}>
                <Title order={4} mb="xs">
                  {t(`subscription.apps.${app.id}.name`)}
                </Title>
                <Text size="sm" c="dimmed" lineClamp={3}>
                  {t(`subscription.apps.${app.id}.text`)}
                </Text>
              </div>
            </Card>
          ))}
        </SimpleGrid>
      </Container>

      <Divider mx="xl" />

      {/* Benefits */}
      <Container size="md" py="xl">
        <Title order={2} ta="center" mb="xl" className={classes.sectionTitle}>
          {t("subscription.benefits.title")}
        </Title>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          {(["benefit1", "benefit2", "benefit3", "benefit4"] as const).map((key) => (
            <Group key={key} align="flex-start" gap="sm" wrap="nowrap">
              <ThemeIcon color="green" variant="light" size="lg" radius="xl" style={{ flexShrink: 0 }}>
                <IconCheck size={16} />
              </ThemeIcon>
              <div>
                <Text fw={600} size="sm">{t(`subscription.benefits.${key}.title`)}</Text>
                <Text size="sm" c="dimmed">{t(`subscription.benefits.${key}.text`)}</Text>
              </div>
            </Group>
          ))}
        </SimpleGrid>
      </Container>

      <Divider mx="xl" />

      {/* Checkout */}
      <Container size="sm" py="xl" id="checkout">
        <Title order={2} ta="center" mb="sm" className={classes.sectionTitle}>
          {t("subscription.checkout.title")}
        </Title>
        <Text ta="center" c="dimmed" mb="xl">
          {t("subscription.checkout.subtitle")}
        </Text>
        <SubscriptionCheckout />
      </Container>
    </div>
  );
}
